import {AlertCircle, CheckCircle2, Info,XCircle} from 'lucide-react';
import {ReactNode, useCallback} from 'react';

import {type ToastProps} from '@/components/ui/toast';
import {Toaster} from '@/components/ui/toaster';
import {useToast} from '@/components/ui/use-toast';
import {cn} from "@/lib/utils";

interface ToastProviderProps {
    children: ReactNode;
}

type ToastVariant = 'success' | 'destructive' | 'warning' | 'info' | 'default';

export const ToastProvider = ({children}: ToastProviderProps) => {
    return (
        <>
            {children}
            <Toaster />
        </>
    );
};

export const useShowToast = () => {
    const { toast } = useToast();

    const getIcon = useCallback((variant: ToastVariant) => {
        const iconClasses = {
            success: "text-emerald-400",
            destructive: "text-red-400",
            warning: "text-amber-400",
            info: "text-blue-400"
        };

        const icons = {
            success: CheckCircle2,
            destructive: XCircle,
            warning: AlertCircle,
            info: Info,
            default: null,
        };

        const IconComponent = icons[variant];
        return IconComponent ? (
            <IconComponent
                className={cn(
                    "h-5 w-5",
                    iconClasses[variant as keyof typeof iconClasses]
                )}
            />
        ) : null;
    }, []);

    const getToastStyles = useCallback((variant: ToastVariant): string => {
        const baseStyles = cn(
            "p-4 rounded-lg shadow-lg",
            "w-full md:w-auto md:min-w-[384px]",
            "max-w-full",
            "bg-gray-900",
            "text-white"
        );

        const variantStyles = {
            success: "",
            destructive: "",
            warning: "",
            info: "",
            default: "",
        };

        return cn(baseStyles, variantStyles[variant]);
    }, []);

    const showToast = useCallback((message: string, title?: string, variant: ToastVariant = 'default') => {
        const toastProps: Partial<ToastProps> & { description: ReactNode } = {
            description: (
                <div className="flex items-start gap-3">
                    {getIcon(variant)}
                    <div className="flex-1">
                        {title && (
                            <p className="font-medium mb-1">{title}</p>
                        )}
                        <p className="text-sm opacity-90 break-words leading-5">
                            {message}
                        </p>
                    </div>
                </div>
            ),
            variant: 'default',
            duration: 2000,
            className: getToastStyles(variant),
        };

        toast(toastProps);
    }, [getIcon, getToastStyles, toast]);

    // Memoize all the exported functions
    const showSuccess = useCallback((message: string, title?: string) =>
        showToast(message, title, 'success'), [showToast]);

    const showError = useCallback((message: string, title?: string) =>
        showToast(message, title, 'destructive'), [showToast]);

    const showWarning = useCallback((message: string, title?: string) =>
        showToast(message, title, 'warning'), [showToast]);

    const showInfo = useCallback((message: string, title?: string) =>
        showToast(message, title, 'info'), [showToast]);

    return {
        showSuccess,
        showError,
        showWarning,
        showInfo,
    };
};